import { useState } from 'react'
import { Info, X } from 'lucide-react'
import { PropertySearchBox } from '@/components/search/PropertySearchBox'
import { GotALetterModal } from '@/components/claim/GotALetterModal'
import { FadeModal } from '@/components/ui/fade-modal'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import biddleLogo from '@/assets/biddle-logo.png'

const WHAT_IS_A_BIDDLE =
  'A Biddle is a non-binding expression of interest — your price and terms, with a private chat if the owner chooses to reply. You can make a Biddle on any property, even if it’s not on the market — the owner might still be interested!'

/**
 * Minimal Google-style search home.
 * Logo: centered on desktop; in the mobile top bar via FoundationShell.
 */
export function FoundationHomePage() {
  const [whatOpen, setWhatOpen] = useState(false)
  const [letterOpen, setLetterOpen] = useState(false)

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col">
      {/* Mobile photographic background */}
      <div className="pointer-events-none absolute inset-0 md:hidden" aria-hidden>
        <img
          src="/search-background.jpg"
          alt=""
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-white/30" />
      </div>

      {/* Desktop photographic background */}
      <div className="pointer-events-none absolute inset-0 hidden md:block" aria-hidden>
        <img
          src="/desktop-background.jpeg"
          alt=""
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-white/30" />
      </div>

      <div className="relative z-10 flex h-full min-h-0 flex-col items-center justify-center px-6 pb-10 pt-6 md:pt-16">
        {/* Nudge down on mobile so the cluster reads as centered in the full viewport (below the top bar). */}
        <div className="mt-20 flex w-full max-w-xl flex-col items-center md:mt-0">
          {/* Desktop: logo, search, and CTAs in one lightly frosted panel. Mobile stays flat. */}
          <div className="flex w-full flex-col items-center md:rounded-3xl md:border md:border-white/40 md:bg-white/40 md:px-10 md:py-12 md:shadow-md md:backdrop-blur-[2px]">
            <img
              src={biddleLogo}
              alt="Biddle"
              className="mb-10 hidden h-20 w-auto md:block"
            />
            <PropertySearchBox
              placeholder="Search for any address"
              className="w-full"
            />
            <div className="mt-6 flex flex-col items-center gap-3 text-sm font-bold">
              <button
                type="button"
                onClick={() => setWhatOpen(true)}
                className="inline-flex items-center gap-1.5 text-navy transition hover:text-navy/80"
              >
                What is a Biddle?
                <Info className="h-4 w-4 text-biddle-blue" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => setLetterOpen(true)}
                className="text-navy transition hover:text-navy/80"
              >
                Got a letter?
              </button>
            </div>
          </div>
        </div>
      </div>

      <WhatIsABiddleModal open={whatOpen} onClose={() => setWhatOpen(false)} />
      <GotALetterModal open={letterOpen} onClose={() => setLetterOpen(false)} />
    </div>
  )
}

function WhatIsABiddleModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <FadeModal open={open} onClose={onClose} labelledBy="what-is-biddle-title">
      <Card className="relative w-full border-0 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <CardHeader className="pr-12">
          <CardTitle id="what-is-biddle-title">What is a Biddle?</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm leading-relaxed text-muted">{WHAT_IS_A_BIDDLE}</p>
        </CardContent>
      </Card>
    </FadeModal>
  )
}
